import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import { defaultCouncilFilters } from 'features/ProjectProposalDefense/defaultValues';

import { MultipleSelect, TextareaInput } from 'components/FormControl';
import { TOAST } from 'constant';
import { toast } from 'layout';

function SearchFilters({
  visible,
  setVisible,
  filters,
  setFilters,
  appliedfilters,
  setAppliedFilters,
  councilTypes,
  reviewStatusOptions,
}) {
  // #region Data
  const location = useLocation();
  const { t } = useTranslation();

  const { control, handleSubmit, reset, trigger } = useForm({
    mode: 'onChange',
    defaultValues: defaultCouncilFilters,
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Event
  useEffect(() => {
    reset(filters);
    reset(appliedfilters);
  }, [filters, appliedfilters]);

  const onSubmit = (data) => {
    const submitData = { ...data };

    localStorage.setItem(
      `scientist-${location.pathname.slice(1)}-filters`,
      JSON.stringify(submitData)
    );

    setFilters(submitData);
    setAppliedFilters(submitData);
    setVisible(false);
  };

  const onClear = () => {
    localStorage.setItem(
      `scientist-${location.pathname.slice(1)}-filters`,
      JSON.stringify(defaultCouncilFilters)
    );
    setVisible(false);
    setFilters(defaultCouncilFilters);
    setAppliedFilters(defaultCouncilFilters);
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion Event

  // #region Render
  return (
    <Dialog
      header={<p>{t('projectProposalCouncil.viewList.searchFilters')}</p>}
      visible={visible}
      position="center"
      className="w-6 md:w-6 lg:w-6 mx-1rem"
      onHide={() => setVisible(false)}
      draggable={false}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className="p-fluid"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={checkKeyDown}
        autoComplete="off"
      >
        <div className="grid">
          {/* Vietnamese name */}
          <div className="col-12">
            <TextareaInput
              label={t('projectProposal.viewList.nameVN')}
              name="vietnameseName"
              control={control}
              errors={errors}
            />
          </div>
          {/* Owner name */}
          <div className="col-12">
            <TextareaInput
              label={t('projectProposal.viewList.owner')}
              name="ownerName"
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-12">
            <MultipleSelect
              label={t('council.viewList.councilType')}
              name="councilTypes"
              options={councilTypes}
              inputField="code"
              renderField="name"
              control={control}
              errors={errors}
              panelStyle={{ maxWidth: '680px' }}
            />
          </div>
          <div className="col-12">
            <MultipleSelect
              label={t('projectProposal.viewList.status')}
              name="reviewStatus"
              options={reviewStatusOptions}
              inputField="code"
              renderField="name"
              control={control}
              errors={errors}
              panelStyle={{ maxWidth: '680px' }}
            />
          </div>
        </div>

        <div className="flex justify-content-between">
          <Button
            label={t('projectProposal.viewList.clearFilter')}
            type="button"
            className="w-8rem"
            severity="danger"
            onClick={onClear}
          />

          <div className="flex justify-content-end gap-2">
            <Button
              label={t('formLayout.action.close')}
              type="button"
              className="w-7rem"
              severity="danger"
              onClick={() => setVisible(false)}
            />
            <Button
              label={t('employee.viewList.apply')}
              type="submit"
              className="w-7rem"
              severity="info"
              onClick={checkFormValid}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export default SearchFilters;

SearchFilters.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  appliedfilters: PropTypes.bool.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,

  // eslint-disable-next-line react/require-default-props, react/forbid-prop-types
  councilTypes: PropTypes.array,
  // eslint-disable-next-line react/require-default-props, react/forbid-prop-types
  reviewStatusOptions: PropTypes.array,
};

SearchFilters.defaultProps = {
  councilTypes: [],
  reviewStatusOptions: [],
};
